/**
 * The skills editor on a profile: the held skills as chips, and the catalog
 * picker that adds one.
 *
 * Both TalentProfile and CouncilProfile show it. The council page had no
 * skills at all, so a council member could neither see nor change the skills
 * imported for them from their team's sheet (2026-10-12). Each page owns the
 * section around it; this owns what is inside.
 *
 * `notify` shows each skill's "Notify me" bell. That mail only reaches people
 * in the talent pool, so a council member who is not in it gets no bell.
 */

import { useMemo, useState } from 'react'
import {
  Button,
  Caption1,
  Combobox,
  Option,
  OptionGroup,
  Spinner,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { Dismiss16Regular, FolderRegular } from '@fluentui/react-icons'
import { useSkillSuggestions } from '../hooks/useSkillSuggestions'
import { SkillNotifyToggle } from './SkillNotifyToggle'
import { useToast } from './ToastProvider'
import { useAddSkill, useMySkills, useRemoveSkill } from '../hooks/useUserSkills'
import { useCapabilities } from '../hooks/useCapabilities'
import type { Capability } from '../types'
import { bayerColors } from '../theme'
import { apiErrorMessage } from '../lib/apiError'
import {
  WHOLE_CATEGORY_NOTE,
  groupCatalog,
  hasCategories,
  searchCatalog,
  skillHoverText,
} from '../lib/skillCatalog'

const useStyles = makeStyles({
  skillRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center',
  },
  skillChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '9999px',
    background: bayerColors.navyLight,
    color: bayerColors.navy,
    fontSize: '12px',
    fontWeight: 600,
  },
  // A held category: a folder before its name, and the why on hover.
  skillChipCategory: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  chipRemove: {
    cursor: 'pointer',
    display: 'inline-flex',
    background: 'transparent',
    border: 'none',
    padding: 0,
    color: bayerColors.navy,
    ':hover': { color: '#b10e1c' },
  },
  missingSkillHint: {
    marginTop: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground3,
  },
  // The picker's rows once the catalog has categories: a category is a pick
  // of its own, its skills sit indented under it with a line of description.
  optionIndented: {
    paddingLeft: '28px',
  },
  optionStack: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  optionDesc: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  optionCategory: {
    fontWeight: tokens.fontWeightSemibold,
  },
  optionTag: {
    marginLeft: 'auto',
    paddingLeft: '8px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
  // A real button, not a styled span: this is the only way out for someone
  // whose term is off-catalog, so it has to answer Enter and Space too.
  nudgeSuggestion: {
    fontWeight: tokens.fontWeightSemibold,
    color: bayerColors.navyMid,
    background: 'transparent',
    border: 'none',
    padding: '0 2px',
    cursor: 'pointer',
    textDecorationLine: 'underline',
    ':hover': { color: bayerColors.navy },
  },
  addSkillRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
})

export function ProfileSkills({ notify = true }: { notify?: boolean }) {
  const styles = useStyles()
  const { showToast } = useToast()
  const skillsQuery = useMySkills()
  const capabilitiesQuery = useCapabilities()
  const addSkill = useAddSkill()
  const removeSkill = useRemoveSkill()
  const [capSelection, setCapSelection] = useState<string>('')
  const [capInput, setCapInput] = useState<string>('')

  const mySkillIds = useMemo(
    () => new Set((skillsQuery.data ?? []).map((s) => s.capability_id)),
    [skillsQuery.data],
  )

  // The whole catalog, searched on what is typed (name, description, examples,
  // category), grouped by category, less what is already on the profile. A
  // category already held stays as a heading over its skills. Search ranks
  // exact name first, then prefix, then the rest: the server's order. By name
  // alone, a short exact name such as "R" sank under every name that merely
  // contains the letter. Nothing is cut off: a cap of 50 once hid the
  // alphabetical tail when BROWSING (ticket #21).
  const categorized = hasCategories(capabilitiesQuery.data ?? [])
  const pickerGroups = useMemo(
    () =>
      searchCatalog(groupCatalog(capabilitiesQuery.data ?? []), capInput).flatMap((g) => {
        const category = g.category && !mySkillIds.has(g.category.id) ? g.category : null
        const skills = g.skills.filter((c) => !mySkillIds.has(c.id))
        if (!category && skills.length === 0) return []
        return [
          {
            key: g.category ? `cat-${g.category.id}` : 'rest',
            heading: g.category ? (category ? null : g.category.name) : 'Other',
            category,
            inCategory: g.category !== null,
            skills,
          },
        ]
      }),
    [capabilitiesQuery.data, mySkillIds, capInput],
  )
  // Every pick, in the order shown.
  const capabilityOptions: Capability[] = useMemo(
    () => pickerGroups.flatMap((g) => (g.category ? [g.category, ...g.skills] : g.skills)),
    [pickerGroups],
  )

  // Typing a skill the catalog lacks used to offer "✨ Add … as new skill",
  // which minted a permanent catalog entry from this page. The vocabulary is
  // curated now (#1343), so the answer to a miss is a sentence, not a button:
  // admins add entries, and anyone can report a missing one.
  const typed = capInput.trim()

  // The catalog entry a fully typed name resolves to. Add used to require a
  // click in the dropdown (`disabled={!capSelection}`), so typing "Python" in
  // full and pressing Add did nothing — no chip, no message, no reason. The
  // demand side has accepted an exact match since #1345; this is the same rule
  // on the page #1348 sends 224 people to. Resolved from the not-yet-added
  // pool, so a skill already on the profile is not offered a second time.
  const typedMatch = useMemo(
    () =>
      capabilityOptions.find((c) => c.name.toLowerCase() === typed.toLowerCase()) ?? null,
    [capabilityOptions, typed],
  )
  const heldAlready = useMemo(
    () =>
      (skillsQuery.data ?? []).some(
        (s) => s.capability_name.toLowerCase() === typed.toLowerCase(),
      ),
    [skillsQuery.data, typed],
  )
  const resolvedCapId = capSelection ? Number(capSelection) : (typedMatch?.id ?? 0)

  // A catalog that never arrived is an empty list too, and empty is what a miss
  // looks like — so a failed load must not report your skill as missing from
  // it. A failed REFETCH keeps the last list, which still holds.
  const catalogFailed = capabilitiesQuery.isError && !capabilitiesQuery.data

  // Deliberately only when NOTHING matches: a hint on every prefix would fire
  // while the dropdown is plainly offering the answer. The other dead end —
  // "SQL" typed against a catalog holding "SQL Server", where options exist and
  // Add is still disabled — is answered by the button explaining itself, below.
  const showMissingHint =
    typed.length >= 2 &&
    !typedMatch &&
    !capSelection &&
    !heldAlready &&
    capabilityOptions.length === 0 &&
    !catalogFailed

  // Why Add cannot be pressed. It used to be a plain `disabled`, which is not
  // focusable, so the reason was unreachable by keyboard and absent by mouse.
  const addBlockedReason = !resolvedCapId
    ? typed.length > 0
      ? `Pick a skill from the list — “${typed}” is not a catalog entry on its own.`
      : 'Search the catalog, then pick a skill.'
    : null

  // The server's fuzzy matcher, used here for its NUDGE only. The option list
  // stays the whole catalog filtered locally — browsing has to keep working
  // (ticket #21), and this endpoint answers a search. Without it a typo on
  // this page got "not in the catalog" and nothing else, while the demand side
  // had "did you mean?" all along: the same miss, two different answers, and
  // the worse one on the page #1348 sends people to.
  const nudge = useSkillSuggestions(capInput, typed.length >= 2).data?.nudge ?? null
  const nudgeOptions = useMemo(() => {
    const byName = new Map(
      (capabilitiesQuery.data ?? []).map((c) => [c.name.toLowerCase(), c] as const),
    )
    const seen = new Set<number>()
    return (nudge?.suggestions ?? [])
      .map((s) => byName.get(s.toLowerCase()))
      .filter((c): c is Capability => {
        // The nudge names catalog entries, so a miss here means the two reads
        // disagree; and a skill already held is not a suggestion.
        if (!c || mySkillIds.has(c.id) || seen.has(c.id)) return false
        seen.add(c.id)
        return true
      })
  }, [nudge, capabilitiesQuery.data, mySkillIds])

  const addCapability = (capId: number) => {
    if (!capId) return
    addSkill.mutate(
      { capability_id: capId },
      {
        onSuccess: () => {
          setCapSelection('')
          setCapInput('')
          showToast({ title: 'Skill added', intent: 'success' })
        },
        onError: (err) =>
          showToast({ title: 'Could not add skill', body: apiErrorMessage(err), intent: 'error' }),
      },
    )
  }

  const handleAddSkill = () => addCapability(resolvedCapId)

  // An entry outside the categories, as the picker has always shown it.
  const renderFlatOption = (c: Capability) => (
    <Option key={c.id} value={String(c.id)} text={c.name}>
      {c.name}
      {/* Where the skill lives, so a pick is informed: the same word the
          catalog panel uses for the pool nobody's focus area owns. `text`
          stays the bare name - it is what lands in the input. */}
      <span
        style={{ color: tokens.colorNeutralForeground3, marginLeft: 6 }}
        data-testid={`skill-option-fa-${c.id}`}
      >
        {c.focus_area_code ?? 'Shared'}
      </span>
    </Option>
  )

  // Held entries that are whole categories, for their chip.
  const categoryIds = new Set(
    (capabilitiesQuery.data ?? []).filter((c) => c.is_category).map((c) => c.id),
  )

  return (
    <>
      {skillsQuery.isLoading ? (
        <Spinner size="tiny" label="Loading skills…" />
      ) : skillsQuery.isError ? (
        <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>
          Could not load your skills. Refresh to try again.
        </Caption1>
      ) : (
        <div className={styles.skillRow} role="list" aria-label="Your skills">
          {(skillsQuery.data ?? []).length === 0 ? (
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              No skills yet — add one below.
            </Caption1>
          ) : (
            (skillsQuery.data ?? []).map((s) => (
              <span key={s.id} className={styles.skillChip} role="listitem">
                {categoryIds.has(s.capability_id) ? (
                  <span
                    className={styles.skillChipCategory}
                    title={WHOLE_CATEGORY_NOTE}
                    data-category="true"
                  >
                    <FolderRegular aria-hidden />
                    {s.capability_name}
                  </span>
                ) : (
                  s.capability_name
                )}
                {notify && <SkillNotifyToggle skill={s} />}
                <button
                  type="button"
                  className={styles.chipRemove}
                  aria-label={`Remove ${s.capability_name}`}
                  onClick={() => {
                    removeSkill.mutate(s.id, {
                      // Undo rather than a confirmation (#1245). Re-adding
                      // carries the proficiency and match-notification
                      // settings back, so nothing but the row id differs.
                      onSuccess: () =>
                        showToast({
                          title: `Removed ${s.capability_name}`,
                          action: {
                            label: 'Undo',
                            onClick: () =>
                              addSkill.mutate({
                                capability_id: s.capability_id,
                                proficiency_level: s.proficiency_level,
                                notify_on_match: s.notify_on_match,
                              }),
                          },
                        }),
                      onError: (err) => {
                        showToast({
                          title: 'Could not remove skill',
                          body: apiErrorMessage(err),
                          intent: 'error',
                        })
                      },
                    })
                  }}
                >
                  <Dismiss16Regular />
                </button>
              </span>
            ))
          )}
        </div>
      )}

      <div className={styles.addSkillRow}>
        <Combobox
          aria-label="Add a skill"
          placeholder={
            capabilitiesQuery.isLoading
              ? 'Loading capabilities…'
              : 'Type to search the skills catalog…'
          }
          value={capInput}
          selectedOptions={capSelection ? [capSelection] : []}
          onOptionSelect={(_, data) => {
            setCapSelection(data.optionValue ?? '')
            setCapInput(data.optionText ?? '')
          }}
          onInput={(e) => {
            const v = (e.target as HTMLInputElement).value
            setCapInput(v)
            if (capSelection) setCapSelection('')
          }}
          onKeyDown={(e) => {
            // Only the case Fluent does nothing with: no option chosen,
            // but the text is a catalog entry. With an option highlighted
            // Fluent's own Enter selects it and this must stay out of the
            // way. The demand side has taken Enter since #1345.
            if (e.key === 'Enter' && !capSelection && typedMatch) {
              e.preventDefault()
              addCapability(typedMatch.id)
            }
          }}
          disabled={capabilitiesQuery.isLoading || catalogFailed}
        >
          {capabilityOptions.length === 0 ? (
            <Option value="" disabled>
              No matches
            </Option>
          ) : categorized ? (
            pickerGroups.map((g) => (
              <OptionGroup
                key={g.key}
                label={g.heading ?? undefined}
                aria-label={g.heading ? undefined : g.category?.name}
              >
                {g.category && (
                  <Option
                    value={String(g.category.id)}
                    text={g.category.name}
                    title={WHOLE_CATEGORY_NOTE}
                    data-testid={`skill-option-category-${g.category.id}`}
                  >
                    <FolderRegular aria-hidden />
                    <span className={styles.optionCategory}>{g.category.name}</span>
                    <span className={styles.optionTag}>Whole category</span>
                  </Option>
                )}
                {g.skills.map((c) =>
                  g.inCategory ? (
                    <Option
                      key={c.id}
                      value={String(c.id)}
                      text={c.name}
                      title={skillHoverText(c)}
                      className={styles.optionIndented}
                    >
                      <span className={styles.optionStack}>
                        <span>{c.name}</span>
                        {c.description && (
                          <span className={styles.optionDesc}>{c.description}</span>
                        )}
                      </span>
                    </Option>
                  ) : (
                    renderFlatOption(c)
                  ),
                )}
              </OptionGroup>
            ))
          ) : (
            capabilityOptions.map(renderFlatOption)
          )}
        </Combobox>
        <Tooltip
          content={addBlockedReason ?? 'Add this skill to your profile'}
          relationship="description"
        >
          <Button
            appearance="primary"
            onClick={handleAddSkill}
            disabled={addSkill.isPending}
            disabledFocusable={!resolvedCapId}
          >
            {addSkill.isPending ? 'Adding…' : 'Add skill'}
          </Button>
        </Tooltip>
      </div>
      {catalogFailed && (
        <Caption1
          role="alert"
          data-testid="skill-catalog-error"
          style={{ color: tokens.colorPaletteRedForeground1 }}
        >
          Couldn&apos;t load the skills catalog.{' '}
          <button
            type="button"
            className={styles.nudgeSuggestion}
            onClick={() => void capabilitiesQuery.refetch()}
          >
            Try again
          </button>
        </Caption1>
      )}
      {showMissingHint && (
        <Caption1 className={styles.missingSkillHint} data-testid="skill-not-in-catalog">
          “{typed}” is not in the catalog.{' '}
          {nudgeOptions.length > 0 ? (
            <span data-testid="skill-nudge">
              Did you mean{' '}
              {nudgeOptions.map((c, i) => (
                <span key={c.id}>
                  <button
                    type="button"
                    className={styles.nudgeSuggestion}
                    onClick={() => addCapability(c.id)}
                  >
                    {c.name}
                  </button>
                  {i < nudgeOptions.length - 1 ? ', ' : ''}
                </span>
              ))}
              ?
            </span>
          ) : (
            <>Missing one? Report it through Help and feedback.</>
          )}
        </Caption1>
      )}
      {heldAlready && typed.length >= 2 && !capSelection && (
        <Caption1 className={styles.missingSkillHint} data-testid="skill-already-held">
          “{typed}” is already on your profile.
        </Caption1>
      )}
    </>
  )
}
